/**
 * IndexedDB Manager for POS Offline Support
 * Handles storage and retrieval of offline sales and cached data
 */

const DB_NAME = 'POSOfflineDB';
const DB_VERSION = 1;

// Store names
const STORES = {
  OFFLINE_SALES: 'offlineSales',
  CACHED_PRODUCTS: 'cachedProducts',
  SYNC_QUEUE: 'syncQueue',
  SESSION_DATA: 'sessionData'
};

class OfflineDB {
  constructor() {
    this.db = null;
    this.isReady = false;
  }

  /**
   * Initialize the database
   */
  async init() {
    if (this.db) return this.db;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('[OfflineDB] Error opening database:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.isReady = true;
        console.log('[OfflineDB] Database opened successfully');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        console.log('[OfflineDB] Upgrading database...');

        // Offline Sales Store
        if (!db.objectStoreNames.contains(STORES.OFFLINE_SALES)) {
          const salesStore = db.createObjectStore(STORES.OFFLINE_SALES, { 
            keyPath: 'localId', 
            autoIncrement: true 
          });
          salesStore.createIndex('status', 'status', { unique: false });
          salesStore.createIndex('createdAt', 'createdAt', { unique: false });
          salesStore.createIndex('branchId', 'branchId', { unique: false });
        }

        // Cached Products Store
        if (!db.objectStoreNames.contains(STORES.CACHED_PRODUCTS)) {
          const productsStore = db.createObjectStore(STORES.CACHED_PRODUCTS, { 
            keyPath: 'product_id' 
          });
          productsStore.createIndex('branchId', 'branchId', { unique: false });
        }

        // Sync Queue Store
        if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
          const syncStore = db.createObjectStore(STORES.SYNC_QUEUE, { 
            keyPath: 'id', 
            autoIncrement: true 
          });
          syncStore.createIndex('type', 'type', { unique: false });
          syncStore.createIndex('status', 'status', { unique: false });
        }

        // Session Data Store
        if (!db.objectStoreNames.contains(STORES.SESSION_DATA)) {
          db.createObjectStore(STORES.SESSION_DATA, { keyPath: 'key' });
        }
      };
    });
  }

  /**
   * Ensure database is initialized
   */
  async ensureDB() {
    if (!this.db) {
      await this.init();
    }
    return this.db;
  }

  // ==================== OFFLINE SALES ====================

  /**
   * Save a sale for offline sync
   */
  async saveOfflineSale(saleData) {
    const db = await this.ensureDB();
    
    const offlineSale = {
      ...saleData,
      localId: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      status: 'pending', // pending, syncing, synced, error
      createdAt: new Date().toISOString(),
      syncAttempts: 0,
      lastSyncAttempt: null,
      syncError: null
    };

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.OFFLINE_SALES], 'readwrite');
      const store = transaction.objectStore(STORES.OFFLINE_SALES);
      const request = store.add(offlineSale);

      request.onsuccess = () => {
        console.log('[OfflineDB] Sale saved offline:', offlineSale.localId);
        resolve({ ...offlineSale, id: request.result });
      };

      request.onerror = () => {
        console.error('[OfflineDB] Error saving offline sale:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get all pending offline sales
   */
  async getPendingOfflineSales() {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.OFFLINE_SALES], 'readonly');
      const store = transaction.objectStore(STORES.OFFLINE_SALES);
      const index = store.index('status');
      const request = index.getAll('pending');

      request.onsuccess = () => {
        resolve(request.result || []);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Get all offline sales (any status)
   */
  async getAllOfflineSales() {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.OFFLINE_SALES], 'readonly');
      const store = transaction.objectStore(STORES.OFFLINE_SALES);
      const request = store.getAll();

      request.onsuccess = () => {
        resolve(request.result || []);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Update offline sale status
   */
  async updateOfflineSaleStatus(localId, status, error = null, serverId = null) {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.OFFLINE_SALES], 'readwrite');
      const store = transaction.objectStore(STORES.OFFLINE_SALES);
      
      // First get the sale
      const getRequest = store.get(localId);
      
      getRequest.onsuccess = () => {
        const sale = getRequest.result;
        if (!sale) {
          reject(new Error('Sale not found'));
          return;
        }

        sale.status = status;
        sale.syncAttempts = (sale.syncAttempts || 0) + 1;
        sale.lastSyncAttempt = new Date().toISOString();
        if (error) sale.syncError = error;
        if (serverId) sale.serverId = serverId;

        const updateRequest = store.put(sale);
        updateRequest.onsuccess = () => resolve(sale);
        updateRequest.onerror = () => reject(updateRequest.error);
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  /**
   * Delete synced offline sales
   */
  async deleteSyncedSales() {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.OFFLINE_SALES], 'readwrite');
      const store = transaction.objectStore(STORES.OFFLINE_SALES);
      const index = store.index('status');
      const request = index.openCursor(IDBKeyRange.only('synced'));

      let deleted = 0;
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          deleted++;
          cursor.continue();
        } else {
          console.log(`[OfflineDB] Deleted ${deleted} synced sales`);
          resolve(deleted);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get offline sales count by status
   */
  async getOfflineSalesCount() {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.OFFLINE_SALES], 'readonly');
      const store = transaction.objectStore(STORES.OFFLINE_SALES);
      const request = store.getAll();

      request.onsuccess = () => {
        const sales = request.result || [];
        const counts = {
          total: sales.length,
          pending: sales.filter(s => s.status === 'pending').length,
          syncing: sales.filter(s => s.status === 'syncing').length,
          synced: sales.filter(s => s.status === 'synced').length,
          error: sales.filter(s => s.status === 'error').length
        };
        resolve(counts);
      };

      request.onerror = () => reject(request.error);
    });
  }

  // ==================== CACHED PRODUCTS ====================

  /**
   * Cache products for offline use
   */
  async cacheProducts(products, branchId) {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.CACHED_PRODUCTS], 'readwrite');
      const store = transaction.objectStore(STORES.CACHED_PRODUCTS);

      // Clear existing products for this branch
      const index = store.index('branchId');
      const clearRequest = index.openCursor(IDBKeyRange.only(branchId));
      
      clearRequest.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };

      // Add new products
      let added = 0;
      products.forEach((product) => {
        const productWithBranch = { ...product, branchId, cachedAt: new Date().toISOString() };
        store.put(productWithBranch);
        added++;
      });

      transaction.oncomplete = () => {
        console.log(`[OfflineDB] Cached ${added} products for branch ${branchId}`);
        resolve(added);
      };

      transaction.onerror = () => reject(transaction.error);
    });
  }

  /**
   * Get cached products for a branch
   */
  async getCachedProducts(branchId) {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.CACHED_PRODUCTS], 'readonly');
      const store = transaction.objectStore(STORES.CACHED_PRODUCTS);
      const index = store.index('branchId');
      const request = index.getAll(branchId);

      request.onsuccess = () => {
        resolve(request.result || []);
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Update cached product quantity (after offline sale)
   */
  async updateCachedProductQuantity(productId, quantitySold) {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.CACHED_PRODUCTS], 'readwrite');
      const store = transaction.objectStore(STORES.CACHED_PRODUCTS);
      const request = store.get(productId);

      request.onsuccess = () => {
        const product = request.result;
        if (product) {
          product.quantity = Math.max(0, (product.quantity || 0) - quantitySold);
          const updateRequest = store.put(product);
          updateRequest.onsuccess = () => resolve(product);
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          resolve(null);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // ==================== SESSION DATA ====================

  /**
   * Save session data
   */
  async saveSessionData(key, data) {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.SESSION_DATA], 'readwrite');
      const store = transaction.objectStore(STORES.SESSION_DATA);
      const request = store.put({ key, data, updatedAt: new Date().toISOString() });

      request.onsuccess = () => resolve(data);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get session data
   */
  async getSessionData(key) {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.SESSION_DATA], 'readonly');
      const store = transaction.objectStore(STORES.SESSION_DATA);
      const request = store.get(key);

      request.onsuccess = () => {
        resolve(request.result?.data || null);
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear all data
   */
  async clearAll() {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const storeNames = Object.values(STORES);
      const transaction = db.transaction(storeNames, 'readwrite');

      storeNames.forEach((storeName) => {
        transaction.objectStore(storeName).clear();
      });

      transaction.oncomplete = () => {
        console.log('[OfflineDB] All data cleared');
        resolve();
      };

      transaction.onerror = () => reject(transaction.error);
    });
  }
}

// Export singleton instance
const offlineDB = new OfflineDB();
export default offlineDB;
